import type { Editor } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { countTextMatches, findTextMatchRange } from "~shared/textMatching";
import { stripMarkdownForHighlightSnippet } from "~shared/agentHighlightSnippet";
import {
  manuscriptPassagesMatchForReplace,
  manuscriptLocatedSpanCoversPassage,
} from "~shared/manuscriptPassageMatch";
import { findPassageRangeInHaystackSlice } from "~shared/manuscriptPassageBreakpoints";
import {
  buildEditorPlainText,
  pmRangeForEditorPlainRange,
} from "~shared/editorPlainText";
import {
  buildManuscriptReplacementInsertContent,
  getInsertedSelectionLength,
} from "src/utils/tiptapSuggestion";
import {
  docRangeHasInsertedMark,
  findAgentEditHighlightRange,
  findFirstTextRangeInDoc,
  pmRangeForReplacementInsert,
} from "../../../src/lib/agentManuscriptHighlight";
import { renderMarkdownForTiptapInsert } from "./renderMarkdownForTiptap";

export type ApplyAgentManuscriptReplacementDetail = {
  documentId: string;
  oldText: string;
  newText: string;
  plainTextStart?: number;
  plainTextEnd?: number;
  pmFrom?: number;
  pmTo?: number;
};

function selectionTextMatchesReplacePassage(selectionText: string, passage: string): boolean {
  return manuscriptPassagesMatchForReplace(selectionText, passage);
}

function selectionSpanCoversReplacePassage(locatedText: string, passage: string): boolean {
  return manuscriptLocatedSpanCoversPassage(locatedText, passage);
}

function docRangeCoversReplacePassage(
  doc: ProseMirrorNode,
  range: { from: number; to: number },
  passage: string,
): boolean {
  const slice = doc.textBetween(range.from, range.to, "\n", "\0").replace(/\0/g, "");
  return selectionSpanCoversReplacePassage(slice, passage);
}

function findAnchoredDocRange(
  doc: ProseMirrorNode,
  oldText: string,
  plainTextStart?: number,
  plainTextEnd?: number,
): { from: number; to: number } | null {
  if (!oldText) return null;

  const plain = buildEditorPlainText(doc);
  const hasAnchors = Number.isFinite(plainTextStart) && Number.isFinite(plainTextEnd);
  const hintOffset = hasAnchors ? plainTextStart : undefined;

  for (const mode of ["exact", "normalized", "whitespace_agnostic"] as const) {
    const matchCount = countTextMatches(plain, oldText, { mode });
    if (matchCount === 0) continue;
    if (matchCount > 1 && hintOffset === undefined) continue;

    const plainRange = findTextMatchRange(plain, oldText, { mode, hintOffset });
    if (!plainRange) continue;

    if (
      hasAnchors
      && mode === "exact"
      && (plainRange.start !== plainTextStart || plainRange.end !== plainTextEnd)
      && matchCount !== 1
    ) {
      continue;
    }

    const mapped = pmRangeForEditorPlainRange(doc, plainRange.start, plainRange.end);
    if (!mapped) continue;

    if (!docRangeCoversReplacePassage(doc, mapped, oldText)) continue;

    return mapped;
  }

  const spacerMatch = findPassageRangeInHaystackSlice(
    plain,
    0,
    plain.length,
    oldText,
    hintOffset,
  );
  if (spacerMatch) {
    const mapped = pmRangeForEditorPlainRange(doc, spacerMatch.start, spacerMatch.end);
    if (mapped && docRangeCoversReplacePassage(doc, mapped, oldText)) {
      return mapped;
    }
  }

  return null;
}

type EditorChainWithInserted = ReturnType<Editor["chain"]> & {
  setInserted?: () => EditorChainWithInserted;
};

/**
 * Best-effort local replace before server Yjs sync. Skips when the target span is missing or ambiguous.
 * Applies the `inserted` mark so optimistic edits match server purple diff styling.
 */
export function applyAgentManuscriptReplacementInEditor(
  editor: Editor,
  detail: ApplyAgentManuscriptReplacementDetail,
): boolean {
  const oldText = String(detail.oldText ?? "");
  const newText = String(detail.newText ?? "");
  if (!oldText || !editor?.view) return false;
  // The server also writes through Yjs. A delivered replacement must not be
  // replayed against its own prefix using the original selection coordinates.
  if (isAgentManuscriptReplacementSyncedInEditor(editor, detail, newText)) return true;

  const doc = editor.state.doc;
  let range: { from: number; to: number } | null = null;

  const pmFrom = Number(detail.pmFrom);
  const pmTo = Number(detail.pmTo);
  if (Number.isFinite(pmFrom) && Number.isFinite(pmTo) && pmTo > pmFrom) {
    try {
      const selected = doc.textBetween(pmFrom, pmTo, "\n", "\0").replace(/\0/g, "");
      if (
        selectionTextMatchesReplacePassage(selected, oldText)
        || selectionSpanCoversReplacePassage(selected, oldText)
      ) {
        range = { from: Math.floor(pmFrom), to: Math.floor(pmTo) };
      }
    } catch {
      range = null;
    }
  }

  if (
    !range
    && Number.isFinite(detail.plainTextStart)
    && Number.isFinite(detail.plainTextEnd)
    && Number(detail.plainTextEnd) > Number(detail.plainTextStart)
  ) {
    const mapped = pmRangeForEditorPlainRange(
      doc,
      Number(detail.plainTextStart),
      Number(detail.plainTextEnd),
    );
    if (mapped && docRangeCoversReplacePassage(doc, mapped, oldText)) {
      range = mapped;
    }
  }

  if (!range) {
    range = findAnchoredDocRange(
      doc,
      oldText,
      detail.plainTextStart,
      detail.plainTextEnd,
    );
  }
  if (!range) return false;

  const renderedMarkdownHtml = renderMarkdownForTiptapInsert(newText);
  const { cleanSuggestion, contentToInsert } =
    buildManuscriptReplacementInsertContent(newText, renderedMarkdownHtml);

  try {
    const insertFrom = range.from;
    const docBefore = editor.state.doc;
    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .deleteSelection()
      .insertContent(contentToInsert)
      .run();

    const docAfter = editor.state.doc;
    const replacementInsertRange = docAfter.content.size !== docBefore.content.size
      ? pmRangeForReplacementInsert(docBefore, docAfter, range)
      : null;
    let postInsertRange: { from: number; to: number } | null = replacementInsertRange;
    if (!postInsertRange) {
      try {
        postInsertRange = findFirstTextRangeInDoc(docAfter, cleanSuggestion);
      } catch {
        postInsertRange = null;
      }
    }
    const lengthDerivedInsertTo = insertFrom + getInsertedSelectionLength(contentToInsert, cleanSuggestion);
    const insertTo = (
      postInsertRange
      && postInsertRange.from >= insertFrom - 2
      && postInsertRange.to > postInsertRange.from
    )
      ? postInsertRange.to
      : lengthDerivedInsertTo;
    const markFrom = (
      postInsertRange
      && postInsertRange.from >= insertFrom - 2
      && postInsertRange.to > postInsertRange.from
    )
      ? postInsertRange.from
      : insertFrom;
    if (!Number.isFinite(insertTo) || insertTo <= markFrom) return true;

    const markChain = editor
      .chain()
      .focus()
      .setTextSelection({ from: markFrom, to: insertTo }) as EditorChainWithInserted;
    if (typeof markChain.setInserted === "function") {
      markChain.setInserted();
    }
    markChain
      .setTextSelection({ from: insertTo, to: insertTo })
      .scrollIntoView()
      .run();
    return true;
  } catch {
    return false;
  }
}

/**
 * True when the complete replacement is already inserted at the target, even if
 * it contains the original text. For drifted targets, retain the legacy check that
 * the old passage is gone and the replacement snippet carries inserted marks.
 */
export function isAgentManuscriptReplacementSyncedInEditor(
  editor: Editor,
  detail: ApplyAgentManuscriptReplacementDetail,
  newTextSnippet: string,
): boolean {
  const snippet = String(newTextSnippet ?? "").trim();
  if (!snippet || !editor?.view) return false;

  const doc = editor.state.doc;
  const fullReplacement = stripMarkdownForHighlightSnippet(String(detail.newText ?? "")).trim();
  const pmFrom = Number(detail.pmFrom);
  const hasValidPmStart = Number.isInteger(pmFrom) && pmFrom >= 0 && pmFrom <= doc.content.size;
  const anchorStart = Number.isInteger(detail.plainTextStart) && Number(detail.plainTextStart) >= 0
    ? Number(detail.plainTextStart)
    : hasValidPmStart
      ? doc.textBetween(0, pmFrom, "\n").length
      : undefined;
  if (fullReplacement && anchorStart !== undefined) {
    const plain = buildEditorPlainText(doc);
    const match = findTextMatchRange(plain, fullReplacement, {
      mode: "normalized", hintOffset: anchorStart,
    });
    if (match?.start === anchorStart) {
      // Inserted marks can belong to an earlier edit. A pending shortening
      // still has its complete old passage here, beyond the new-text prefix.
      const oldPassage = stripMarkdownForHighlightSnippet(String(detail.oldText ?? "")).trim();
      const oldMatch = oldPassage ? findTextMatchRange(plain, oldPassage, {
        mode: "normalized", hintOffset: anchorStart,
      }) : null;
      if (oldMatch?.start === anchorStart && oldMatch.end > match.end) return false;
      const range = pmRangeForEditorPlainRange(doc, match.start, match.end);
      if (range && docRangeHasInsertedMark(doc, range.from, range.to)) return true;
    }
  }

  const oldText = String(detail.oldText ?? "");
  if (oldText) {
    const oldStillPresent = findAnchoredDocRange(
      editor.state.doc,
      oldText,
      detail.plainTextStart,
      detail.plainTextEnd,
    );
    if (oldStillPresent) return false;
  }

  const range = findAgentEditHighlightRange(editor.state.doc, snippet);
  if (!range) return false;
  return docRangeHasInsertedMark(editor.state.doc, range.from, range.to);
}
